import React from "react";
import { Phone, X, Heart, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const RESOURCES = [
  { flag: "🇺🇸", country: "United States", number: "988", label: "Suicide & Crisis Lifeline (call or text)", href: "tel:988" },
  { flag: "🇬🇧", country: "United Kingdom", number: "116 123", label: "Samaritans", href: "tel:116123" },
  { flag: "🇦🇺", country: "Australia", number: "13 11 14", label: "Lifeline", href: "tel:131114" },
  { flag: "🇨🇦", country: "Canada", number: "988", label: "Suicide Crisis Helpline", href: "tel:988" },
];

/**
 * Crisis resources modal — shown when the backend flags distress in a
 * user's reflection answer. Copy is deliberately gentle. Umbra is a
 * journaling tool, not a substitute for professional care.
 */
export default function CrisisModal({ open, onDismiss }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onDismiss?.(); }}>
      <DialogContent
        data-testid="crisis-modal"
        className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full bg-obsidian-800 border-obsidian-600 rounded-none p-0 [&>button.absolute]:hidden max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <VisuallyHidden asChild>
          <DialogTitle>Crisis resources</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription>Umbra care log — hotlines and support numbers</DialogDescription>
        </VisuallyHidden>
        <div className="p-6 sm:p-8 md:p-10">
          <div className="flex items-start justify-between gap-4 mb-5 sm:mb-6">
            <div className="flex items-center gap-3">
              <Heart className="h-5 w-5 text-terracotta" />
              <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta">
                Take a breath with me
              </p>
            </div>
          </div>

          <h2 className="font-serif text-xl sm:text-2xl md:text-3xl text-bone-100 leading-snug mb-4">
            What you wrote is heavy. You are not alone with it.
          </h2>

          <p className="font-serif italic text-sm sm:text-base text-bone-300 leading-relaxed mb-5 sm:mb-6">
            Umbra is a journaling tool, not a substitute for professional care.
            If you're in crisis or thinking of harming yourself, please talk to a real person
            right now. It doesn't have to be forever — just this next hour.
          </p>

          <div className="space-y-3 mb-5 sm:mb-6" data-testid="crisis-resources">
            {RESOURCES.map((r) => (
              <a
                key={r.country}
                href={r.href}
                data-testid={`crisis-${r.country.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex items-center justify-between gap-3 border border-obsidian-600 hover:border-terracotta p-4 group transition-colors"
              >
                <div>
                  <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 mb-1">
                    {r.flag} {r.country}
                  </p>
                  <p className="font-serif text-lg text-bone-100">{r.number}</p>
                  <p className="font-sans text-xs text-bone-400">{r.label}</p>
                </div>
                <Phone className="h-4 w-4 text-terracotta group-hover:scale-110 transition-transform" />
              </a>
            ))}
            <a
              href="https://findahelpline.com"
              target="_blank"
              rel="noreferrer"
              data-testid="crisis-international"
              className="flex items-center justify-between gap-3 border border-obsidian-600 hover:border-terracotta p-4 group transition-colors"
            >
              <div>
                <p className="font-sans text-[0.65rem] uppercase tracking-[0.2em] text-bone-500 mb-1">
                  🌐 Anywhere else
                </p>
                <p className="font-serif text-lg text-bone-100">findahelpline.com</p>
                <p className="font-sans text-xs text-bone-400">International directory of hotlines</p>
              </div>
              <ExternalLink className="h-4 w-4 text-terracotta group-hover:scale-110 transition-transform" />
            </a>
          </div>

          <div className="border-t border-obsidian-600 pt-4 flex items-center justify-between gap-3 sm:gap-4 flex-wrap">
            <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-bone-500 flex-1 min-w-0">
              This moment was recorded internally so Umbra can improve care.
            </p>
            <button
              onClick={onDismiss}
              data-testid="crisis-dismiss-btn"
              className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] text-bone-400 hover:text-bone-100 transition-colors min-h-[44px] px-1"
            >
              <X className="h-3 w-3" /> I'm safe, continue
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
